import React from 'react';
import { toast } from 'react-toastify';
import { ClientError, useLocale } from 'shared-ui';

import { useMutation } from 'hooks/useMutation/useMutation';
import { downloadKadXlsxReport } from 'api/cards/cards';
import { downloadPkdXlsxReport } from 'api/owners/owners';
import { downloadEventsXlsxReport } from 'api/events/events';
import { downloadInneEventsReport } from 'api/reports/reports';

import { DownloadTable } from './DownloadTable';
import type { DownloadTableContainerProps } from './DownloadTable.types';

// The frontend has no own `axios` dependency (shared-ui owns the client), so importing it broke the
// production build; recognise the client's error by its `isAxiosError` flag instead.
const isClientError = (error: unknown): error is ClientError =>
  typeof error === 'object' && error !== null && (error as ClientError).isAxiosError === true;

export const DownloadTableContainer: React.FC<DownloadTableContainerProps> = (props: DownloadTableContainerProps) => {
  const { formatMessage } = useLocale();
  const messageId =
    props.type === 'cards'
      ? 'common.downloadTable.cards.success'
      : props.type === 'owners'
      ? 'common.downloadTable.owners.success'
      : 'common.downloadTable.events.success';

  const getFormattedFilename = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day}T${hours}${minutes}${seconds}.${milliseconds}.xlsx`;
  };

  const handleDownloadSuccess = async (response: ArrayBuffer) => {
    try {
      const blob = new Blob([new Uint8Array(response)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Use showSaveFilePicker to let user choose directory and filename
      const handle = await window.showSaveFilePicker({
        suggestedName: getFormattedFilename(),
        startIn: 'downloads',
        types: [
          {
            description: 'Excel Spreadsheet',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            },
          },
        ],
      });

      // Create a writable stream and write the blob
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();

      toast.dark(formatMessage({ id: messageId }));
    } catch (err) {
      // If showSaveFilePicker is not supported or user cancels, fall back to the old method
      if (err instanceof Error && err.name === 'AbortError') {
        // User cancelled the save dialog
        return;
      }

      // Fallback for browsers that don't support showSaveFilePicker
      const blob = new Blob([new Uint8Array(response)], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFormattedFilename();
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  };

  const { mutate: downloadCardsReport, isLoading: isCardsLoading } = useMutation(downloadKadXlsxReport, {
    onSuccess: handleDownloadSuccess,
  });

  const { mutate: downloadPkdReport, isLoading: isPkdLoading } = useMutation(downloadPkdXlsxReport, {
    onSuccess: handleDownloadSuccess,
  });

  const { mutate: downloadEventsReport, isLoading: isEventsLoading } = useMutation(downloadEventsXlsxReport, {
    onSuccess: handleDownloadSuccess,
  });

  const { mutate: downloadInneReport, isLoading: isInneLoading } = useMutation(downloadInneEventsReport, {
    onSuccess: handleDownloadSuccess,
    handleErrorManually: true,
    onError: (error) => {
      if (!isClientError(error)) {
        toast.dark(formatMessage({ id: 'error.unexpectedError' }));
        return;
      }

      const responseData = error.response?.data;
      let responseBody: { error?: string; message?: string } | undefined;

      try {
        const text =
          responseData instanceof ArrayBuffer
            ? new TextDecoder().decode(responseData)
            : typeof responseData === 'string'
            ? responseData
            : undefined;
        responseBody = text ? JSON.parse(text) : undefined;
      } catch {
        responseBody = undefined;
      }

      const message =
        responseBody?.error === 'NO_DATA_FOR_CRITERIA'
          ? responseBody.message || formatMessage({ id: 'reports.error.noData' })
          : responseBody?.message || formatMessage({ id: 'error.unexpectedError' });
      toast.dark(message);
    },
  });

  const handleDownload = () => {
    if (props.type === 'cards') {
      downloadCardsReport({
        ...props.query,
        archiwalna: props.archived,
      });
    } else if (props.type === 'owners') {
      downloadPkdReport({
        query: props.query.query,
        komorka: props.query.komorka?.join(','),
        emptyKomorka: props.query.emptyKomorka,
        stanowisko: props.query.stanowisko?.map((s) => s.id).join(','),
        karta: props.query.karta,
        sort: props.query.sort,
        archiwalny: props.archived,
      });
    } else if (props.type === 'events') {
      downloadEventsReport({
        body: {
          query: props.query.query,
          sort: props.query.sort?.map((s) => s.toString()),
          dataOd: props.query.dataOd ? new Date(props.query.dataOd) : undefined,
          dataDo: props.query.dataDo ? new Date(props.query.dataDo) : undefined,
          mod: props.query.mod,
          tryb: props.query.tryb,
          zdarzenie: props.query.zdarzenie,
          rcp: props.query.rcp,
          rewizja: props.query.rewizja,
          poszukiwany: props.query.poszukiwany,
          serwer: props.query.serwer,
          tkd: props.query.tkd?.value?.toString(),
          limit: props.query.limit,
        },
      });
    } else if (props.type === 'inneEvents') {
      downloadInneReport({
        dataOd: props.query.dataOd,
        dataDo: props.query.dataDo,
        numerKarty: props.query.query,
        kategoria: props.query.kategoria,
        serwer: props.query.serwer,
        czytnik: props.query.tkd?.value?.toString(),
        // An empty list downloads an empty file, like Bieżące and Historia (WKD-2511).
        allowEmpty: true,
      });
    }
  };

  return (
    <DownloadTable
      onDownload={handleDownload}
      isLoading={isCardsLoading || isPkdLoading || isEventsLoading || isInneLoading}
    />
  );
};
